import { IStorytellingSearchRequest } from '../../models/api/storytelling';
import { getProjectFacilityOptions } from '../project-lifecycle/data';

/**
 * The Storytelling card's filters as the form holds them: strings throughout, so a field is
 * "not set" exactly when it is blank. Dates are `YYYY-MM-DD`; the money and square footage fields
 * hold digits only (see `digitsOnly`).
 */
export interface StorytellingFilters {
  projectLocation: string;
  /** A state_or_province id. */
  stateOrProvinceId: string;
  clientName: string;
  projectType: string;
  facilityTypes: string[];
  serviceTypes: string[];
  startDateFrom: string;
  startDateTo: string;
  completionDateFrom: string;
  completionDateTo: string;
  constructionCostMin: string;
  constructionCostMax: string;
  deliveryMethod: string;
  squareFootageMin: string;
  squareFootageMax: string;
  constructionType: string;
}

export const EMPTY_STORYTELLING_FILTERS: StorytellingFilters = {
  projectLocation: '',
  stateOrProvinceId: '',
  clientName: '',
  projectType: '',
  facilityTypes: [],
  serviceTypes: [],
  startDateFrom: '',
  startDateTo: '',
  completionDateFrom: '',
  completionDateTo: '',
  constructionCostMin: '',
  constructionCostMax: '',
  deliveryMethod: '',
  squareFootageMin: '',
  squareFootageMax: '',
  constructionType: '',
};

const NUMBER_KEYS = [
  'constructionCostMin',
  'constructionCostMax',
  'squareFootageMin',
  'squareFootageMax',
] as const;

/**
 * The facility types still on offer after the project type changes, as the Project Request form
 * narrows them: `selected` without any that `projectType` no longer lists. The four general
 * options are on offer whatever the project type, so those stay.
 */
export const pruneFacilityTypes = (selected: string[], projectType: string): string[] => {
  const allowed = new Set(getProjectFacilityOptions(projectType || undefined).map((o) => o.value));
  return selected.filter((value) => allowed.has(value));
};

/** What a number field keeps of what was typed or pasted: `$1,250,000` becomes `1250000`. */
export const digitsOnly = (value: string) => value.replace(/\D/g, '');

const isSet = (value: string) => value.trim() !== '';

/** Whether any filter is set -- what "Search" waits for. */
export const hasStorytellingFilter = (filters: StorytellingFilters) =>
  Object.values(filters).some((value) => (Array.isArray(value) ? value.length > 0 : isSet(value)));

const RANGES: {
  from: keyof StorytellingFilters;
  to: keyof StorytellingFilters;
  label: string;
}[] = [
  { from: 'startDateFrom', to: 'startDateTo', label: 'Start Date' },
  { from: 'completionDateFrom', to: 'completionDateTo', label: 'Completion Date' },
  { from: 'constructionCostMin', to: 'constructionCostMax', label: 'Construction Cost' },
  { from: 'squareFootageMin', to: 'squareFootageMax', label: 'Square Footage' },
];

/**
 * The first reason the filters cannot be searched, or null. The only one is a range whose ends
 * are reversed, which the API refuses too; catching it here keeps the user from learning of it
 * through a failed request.
 */
export const getStorytellingFilterError = (filters: StorytellingFilters): string | null => {
  for (const { from, to, label } of RANGES) {
    const low = filters[from] as string;
    const high = filters[to] as string;
    if (!isSet(low) || !isSet(high)) continue;
    // Both are `YYYY-MM-DD` or digits, so a number compares the amounts and a string the dates.
    const reversed = NUMBER_KEYS.includes(from as typeof NUMBER_KEYS[number])
      ? Number(low) > Number(high)
      : low > high;
    if (reversed) return `${label}: the first value must not be after the second.`;
  }
  return null;
};

/** The request for `filters`, leaving out every blank one. */
export const buildStorytellingSearchRequest = (
  filters: StorytellingFilters,
): IStorytellingSearchRequest => {
  const request: IStorytellingSearchRequest = {};

  (Object.keys(filters) as (keyof StorytellingFilters)[]).forEach((key) => {
    const raw = filters[key];
    if (Array.isArray(raw)) {
      if (raw.length) Object.assign(request, { [key]: raw });
      return;
    }
    const value = raw.trim();
    if (!value) return;
    if ((NUMBER_KEYS as readonly string[]).includes(key)) {
      Object.assign(request, { [key]: Number(value) });
    } else {
      Object.assign(request, { [key]: value });
    }
  });

  return request;
};

const dollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/** A whole-dollar amount (the A3 holds dollars, not cents), or an em dash when unset. */
export const formatStorytellingDollars = (amount?: number | null) =>
  amount === null || amount === undefined ? '—' : dollars.format(amount);

export const formatStorytellingNumber = (value?: number | null) =>
  value === null || value === undefined ? '—' : value.toLocaleString('en-US');

/** The label of the option with `value`, else the value itself (an option since retired), else a dash. */
export const optionLabel = (
  options: readonly { label: string; value: string }[],
  value?: string | null,
) => {
  if (!value) return '—';
  return options.find((option) => option.value === value)?.label ?? value;
};

/**
 * A result's project type for display: the form's label, or for "Other" what the form says it is
 * ("Other (Stadium)").
 */
export const projectTypeLabel = (
  options: readonly { label: string; value: string }[],
  type?: string | null,
  other?: string | null,
) => {
  const label = optionLabel(options, type);
  return type === 'other' && other ? `${label} (${other})` : label;
};

/** Where a result opens: the project's A3 Summary. */
export const storytellingResultPath = (projectId: string) => `/main/projects/${projectId}/a3`;
